import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";

const STATUS_CONFIG = {
  available: { color: "#4caf50", label: "Disponible", selectable: true },
  noPrice:   { color: "#ff9800", label: "Sin precio", selectable: false },
  pending:   { color: "#2196f3", label: "Pendiente", selectable: false },
  sold:      { color: "#f44336", label: "Vendido/Activo", selectable: false },
};

const getStatus = (lote) => {
  if (lote.ESTATUS === "D" && lote.COSTO && Number(lote.COSTO) > 0) return "available";
  if (lote.ESTATUS === "D") return "noPrice";
  if (lote.ESTATUS === "P") return "pending";
  return "sold";
};

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(2),
  },
  legendBar: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    border: "1px solid #e0e0e0",
  },
  legendChip: {
    margin: theme.spacing(0.5),
    fontWeight: 500,
  },
  lotCard: {
    borderRadius: 12,
    padding: theme.spacing(2, 1),
    textAlign: "center",
    cursor: "default",
    border: "2px solid transparent",
    transition: "all 0.2s ease",
    minHeight: 90,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
  },
  selectable: {
    cursor: "pointer",
    "&:hover": {
      transform: "scale(1.06)",
      boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    },
  },
  selected: {
    border: "3px solid #1a237e",
    boxShadow: "0 4px 14px rgba(26,35,126,0.35)",
    transform: "scale(1.06)",
  },
  lotNumber: {
    fontWeight: 700,
    fontSize: 18,
    color: "#fff",
    textShadow: "0 1px 2px rgba(0,0,0,0.2)",
  },
  lotInfo: {
    fontSize: 11,
    color: "rgba(255,255,255,0.9)",
    marginTop: 4,
    lineHeight: 1.3,
  },
  emptyMessage: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
    backgroundColor: "#fafafa",
    borderRadius: 8,
    border: "1px dashed #ccc",
  },
  summary: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    padding: theme.spacing(1, 0),
    marginTop: theme.spacing(1),
    gap: theme.spacing(2),
  },
}));

const LotGrid = ({ lots, selectedLotId, onSelectLot }) => {
  const classes = useStyles();

  if (!lots || lots.length === 0) {
    return (
      <Typography variant="body2" className={classes.emptyMessage}>
        No hay lotes registrados en esta manzana
      </Typography>
    );
  }

  // Ordenar lotes por número
  const sortedLots = [...lots].sort((a, b) => (a.NUMERO || 0) - (b.NUMERO || 0));

  // Conteo por estatus
  const counts = sortedLots.reduce((acc, lote) => {
    const s = getStatus(lote);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className={classes.container}>
      {/* Leyenda */}
      <div className={classes.legendBar}>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <Chip
            key={key}
            size="small"
            icon={<FiberManualRecordIcon style={{ color: cfg.color, fontSize: 14 }} />}
            label={`${cfg.label} (${counts[key] || 0})`}
            variant="outlined"
            className={classes.legendChip}
          />
        ))}
      </div>

      {/* Grid de lotes */}
      <Grid container spacing={2}>
        {sortedLots.map(lote => {
          const status = getStatus(lote);
          const cfg = STATUS_CONFIG[status];
          const isSelected = lote.ID_LOTE === selectedLotId;

          const tooltipText = cfg.selectable
            ? `Lote #${lote.NUMERO} — ${Number(lote.SUPERFICIE || 0).toLocaleString()} m² — $${Number(lote.COSTO || 0).toLocaleString()} — Click para seleccionar`
            : `Lote #${lote.NUMERO} — ${cfg.label}${lote.ESTATUS === "D" ? " (falta asignar precio)" : ""}`;

          return (
            <Grid item xs={4} sm={3} md={2} key={lote.ID_LOTE}>
              <Tooltip title={tooltipText} arrow placement="top">
                <Box
                  className={`${classes.lotCard} ${cfg.selectable ? classes.selectable : ""} ${isSelected ? classes.selected : ""}`}
                  style={{
                    backgroundColor: cfg.color,
                    opacity: cfg.selectable ? 1 : 0.75,
                  }}
                  onClick={() => {
                    if (cfg.selectable) onSelectLot(lote.ID_LOTE);
                  }}
                >
                  <Typography className={classes.lotNumber}>
                    #{lote.NUMERO}
                  </Typography>
                  {cfg.selectable ? (
                    <Typography className={classes.lotInfo}>
                      {Number(lote.SUPERFICIE || 0).toLocaleString()} m²
                      <br />
                      ${Number(lote.COSTO || 0).toLocaleString()}
                    </Typography>
                  ) : (
                    <Typography className={classes.lotInfo}>
                      {cfg.label}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      {/* Resumen */}
      <div className={classes.summary}>
        <Typography variant="caption" color="textSecondary">
          Total: {sortedLots.length} lotes
        </Typography>
      </div>
    </div>
  );
};

LotGrid.propTypes = {
  lots: PropTypes.array,
  selectedLotId: PropTypes.number,
  onSelectLot: PropTypes.func.isRequired,
};

export default LotGrid;
